/**
 * Sign in with Google on the web: Google Identity Services (GIS, https://accounts.google.com/gsi/client) renders
 * Google's own button and hands back an ID token (`credential`) whose `aud` is our web OAuth client. The server's
 * POST /auth/link/google already accepts that audience (apps/server-cf/src/account/google-signin.ts).
 * The page origin (https://handegg.hustlemania.dev) must be an Authorized JavaScript origin of the web client.
 */
export const GOOGLE_WEB_CLIENT_ID =
  '786590552360-pfhas2t5ggnv5jke0d3rr3hgqkjir0at.apps.googleusercontent.com';
const GSI_SRC = 'https://accounts.google.com/gsi/client';

interface GsiId {
  initialize(o: {
    client_id: string;
    callback: (r: { credential?: string }) => void;
    ux_mode?: 'popup' | 'redirect';
    auto_select?: boolean;
    use_fedcm_for_button?: boolean;
  }): void;
  renderButton(el: HTMLElement, o: Record<string, unknown>): void;
  disableAutoSelect?(): void;
}
type GsiWindow = Window & { google?: { accounts?: { id?: GsiId } } };

let loading: Promise<GsiId> | null = null;

/** Load the GIS script once. Rejects when blocked / offline (the caller shows a note). */
export const loadGsi = (): Promise<GsiId> => {
  const w = window as GsiWindow;
  if (w.google?.accounts?.id) return Promise.resolve(w.google.accounts.id);
  if (loading) return loading;
  loading = new Promise<GsiId>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = GSI_SRC;
    s.async = true;
    s.onload = () => {
      const id = (window as GsiWindow).google?.accounts?.id;
      if (id) resolve(id);
      else reject(new Error('gsi missing'));
    };
    s.onerror = () => {
      loading = null;
      reject(new Error('gsi load failed'));
    };
    document.head.appendChild(s);
  });
  return loading;
};

/** Render Google's branded button into `el`; `onToken` gets the ID token after the user picks an account. */
export const renderGoogleButton = async (
  el: HTMLElement,
  onToken: (idToken: string) => void,
  width = 280,
  theme: 'filled_blue' | 'outline' | 'filled_black' = 'filled_blue',
  logoAlignment: 'left' | 'center' = 'left',
): Promise<void> => {
  const id = await loadGsi();
  id.initialize({
    client_id: GOOGLE_WEB_CLIENT_ID,
    ux_mode: 'popup',
    auto_select: false,
    callback: (r) => {
      if (r.credential) onToken(r.credential);
    },
  });
  el.replaceChildren();
  id.renderButton(el, {
    type: 'standard',
    theme,
    size: 'large',
    shape: 'pill',
    text: 'signin_with',
    logo_alignment: logoAlignment,
    width,
    locale: 'en', // never the browser's language (was rendering Hebrew)
  });
};

/** Sign out: tell GIS not to silently re-select this Google account (no-op when GIS never loaded). */
export const googleWebSignOut = (): void => {
  try {
    (window as GsiWindow).google?.accounts?.id?.disableAutoSelect?.();
  } catch {
    /* ignore */
  }
};
